import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    IconButton,
    Collapse,
    Box,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    useMediaQuery,
    Card,
    CardContent,
    Tooltip,
    Paper,
    Button,
    Menu,
    ListItemIcon
} from '@mui/material';
import { Loading } from '../../../../components/loading/Loading';
import AppSidebar from '../../../../components/navbar/DesktopAppSidebar';
import { fetchWeeklyTimeClocksSnapshotListener } from '../../../../repository/TimeClockDAO';
import moment from 'moment';
import EditTimeClockModal from './modals/EditTimeClockModal';
import { IconArrowDown, IconArrowUp, IconHistory, IconClock, IconEdit, IconPlus } from '@tabler/icons-react';
import { useOrganizationContext } from '../../../../context/organization_context/OrganizationContext';
import { MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import usePermissions from '../../../../utils/permissions/UsePermissions';
import { Modules } from '../../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../../utils/permissions/PermissionTypes';
import AddTimeClockModal from './modals/AddTimeClockModal';
import { handleMenuClick, handleMenuClose } from '../../../../components/utils/MenuHandlers';
import { useUserContext } from '../../../../context/user_context/UserContext';

const AdjustmentHistory = ({ adjustments, employees }) => {
    const { t } = useTranslation();
    if (!adjustments || adjustments.length === 0) return null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000).format('h:mm A');
    };

    const getAdjustmentTypeKey = (type) => {
        switch (type) {
            case 'clock-in':
                return StringKeys.CLOCK_IN;
            case 'clock-out':
                return StringKeys.CLOCK_OUT;
            case 'edit':
                return StringKeys.EDIT;
            case 'creation':
                return StringKeys.MANUAL_ENTRY;
            default:
                return StringKeys.ADJUSTMENT;
        }
    };

    return (
        <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold', color: 'text.secondary' }}>
                {t(StringKeys.ADJUSTMENT_HISTORY)}
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
                {adjustments.map((adjustment, index) => {
                    const performer = employees.find(emp => emp.id === adjustment.performed_by);
                    return (
                        <Box key={index} sx={{ mb: index !== adjustments.length - 1 ? 2 : 0 }}>
                            <Grid container spacing={1} alignItems="center">
                                <Grid item>
                                    <IconHistory size={20} />
                                </Grid>
                                <Grid item xs>
                                    <Typography variant="body2">
                                        <strong>{t(getAdjustmentTypeKey(adjustment.type))}</strong> by {performer?.first_name} {performer?.last_name}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {moment(adjustment.timestamp.seconds * 1000).format('MMM D, YYYY h:mm A')}
                                    </Typography>
                                    {adjustment.reason && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {t(StringKeys.REASON)}: {adjustment.reason}
                                        </Typography>
                                    )}
                                    {adjustment.changes && (
                                        <Box sx={{ mt: 1 }}>
                                            {Object.entries(adjustment.changes).map(([key, value]) => (
                                                <Typography key={key} variant="caption" display="block">
                                                    {t(key.replace('timestamp_', '') === 'in' ? StringKeys.TIME_IN : StringKeys.TIME_OUT)}: {' '}
                                                    <span style={{ textDecoration: 'line-through' }}>
                                                        {formatTimestamp(value.old)}
                                                    </span>
                                                    {' → '}
                                                    {formatTimestamp(value.new)}
                                                </Typography>
                                            ))}
                                        </Box>
                                    )}
                                </Grid>
                            </Grid>
                            {index !== adjustments.length - 1 && <Box sx={{ my: 1, borderBottom: '1px dashed rgba(0, 0, 0, 0.12)' }} />}
                        </Box>
                    );
                })}
            </Paper>
        </Box>
    );
};

const TimeClockEntry = ({ entry, employee, onEdit, expanded, onToggle, entryId }) => {
    const { t } = useTranslation();
    const { hasPermission } = usePermissions();
    const { firebaseUser } = useUserContext();
    const { isUserDepartmentManager } = useOrganizationContext();
    const duration = entry.timestamp_out 
        ? moment.duration(
            moment(entry.timestamp_out.seconds * 1000).diff(
                moment(entry.timestamp_in.seconds * 1000)
            )
        )
        : null;

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000).format('h:mm A');
    };

    const formatTimestampWithDate = (timestamp) => {
        if (!timestamp) return '';
        return moment(timestamp.seconds * 1000 + timestamp.nanoseconds / 1000000).format('MMM D, h:mm A');
    };

    const areDifferentDays = (timestamp1, timestamp2) => {
        if (!timestamp1 || !timestamp2) return false;
        const date1 = moment(timestamp1.seconds * 1000).startOf('day');
        const date2 = moment(timestamp2.seconds * 1000).startOf('day');
        return !date1.isSame(date2);
    };

    const canEdit = hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_ALL_TIME_CLOCKS) || 
                   (hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_TIME_CLOCKS_FOR_MANAGED_EMPLOYEES) && 
                    isUserDepartmentManager(firebaseUser?.uid));

    return (
        <Card sx={{ mb: 2 }}>
            <CardContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid item>
                        <IconClock size={24} />
                    </Grid>
                    <Grid item xs>
                        <Typography variant="subtitle1">
                            {areDifferentDays(entry.timestamp_in, entry.timestamp_out) ? (
                                <>
                                    {formatTimestampWithDate(entry.timestamp_in)} - {' '}
                                    {entry.timestamp_out ? formatTimestampWithDate(entry.timestamp_out) : t(StringKeys.CURRENTLY_CLOCKED_IN)}
                                </>
                            ) : (
                                <>
                                    {formatTimestamp(entry.timestamp_in)} - {' '}
                                    {entry.timestamp_out ? formatTimestamp(entry.timestamp_out) : t(StringKeys.CURRENTLY_CLOCKED_IN)}
                                </>
                            )}
                        </Typography>
                        {duration && (
                            <Typography variant="body2" color="text.secondary">
                                {t(StringKeys.DURATION)}: {Math.floor(duration.asHours())}h {duration.minutes()}m
                            </Typography>
                        )}
                    </Grid>
                    <Grid item>
                        {canEdit && (
                            <Tooltip title={t(StringKeys.EDIT)}>
                                <IconButton size="small" onClick={() => onEdit(entry)}>
                                    <IconEdit size={20} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title={expanded ? t(StringKeys.HIDE_DETAILS) : t(StringKeys.SHOW_DETAILS)}>
                            <IconButton size="small" onClick={() => onToggle(entryId)}>
                                {expanded ? <IconArrowUp size={20} /> : <IconArrowDown size={20} />}
                            </IconButton>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 2 }}>
                        {entry.adjustments && <AdjustmentHistory adjustments={entry.adjustments} employees={employee} />}
                    </Box>
                </Collapse>
            </CardContent>
        </Card>
    );
};

function ReviewTimeClocks() {
    const { t } = useTranslation();
    const { organization, organizationEmployees, isUserDepartmentManager } = useOrganizationContext();
    const { firebaseUser } = useUserContext();
    const { hasPermission } = usePermissions();
    const [currentDate, setCurrentDate] = useState(moment());
    const [timeClocks, setTimeClocks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedTimeClock, setSelectedTimeClock] = useState({ entry: null, docId: null });
    const [showEditTimeClockModal, setShowEditTimeClockModal] = useState(false);
    const [expandedEntries, setExpandedEntries] = useState(new Set());
    const [expandedDates, setExpandedDates] = useState(new Set());
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const isMobile = useMediaQuery('(max-width:600px)');
    const [showAddTimeClockModal, setShowAddTimeClockModal] = useState(false);
    const [expandedEmployees, setExpandedEmployees] = useState(new Set());
    const [anchorEl, setAnchorEl] = useState(null);

    const canReadAllTimeClocks = hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_ALL_TIME_CLOCKS) ||
                                hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_ALL_TIME_CLOCKS);
    const canReadManagedTimeClocks = hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_TIME_CLOCKS_FOR_MANAGED_EMPLOYEES);

    const { filteredTimeClocks, managedEmployees } = React.useMemo(() => {
        if (!timeClocks || !firebaseUser || !organizationEmployees) {
            return { filteredTimeClocks: [], managedEmployees: [] };
        }

        if (canReadAllTimeClocks) {
            // Users with read all permissions see all time clocks
            return {
                filteredTimeClocks: timeClocks,
                managedEmployees: organizationEmployees.filter(emp => emp.id !== firebaseUser.uid)
            };
        } 
        
        if (canReadManagedTimeClocks && isUserDepartmentManager(firebaseUser.uid)) {
            // For department managers, only show time clocks from their department
            const managerEmployee = organizationEmployees.find(emp => emp.id === firebaseUser.uid);
            const departmentId = managerEmployee?.department_id;
            
            const departmentEmployees = organizationEmployees.filter(emp => 
                emp.department_id === departmentId && emp.id !== firebaseUser.uid
            );
            
            const departmentTimeClocks = timeClocks.filter(timeClock => 
                departmentEmployees.some(emp => emp.id === timeClock.employee_id)
            );
            
            return {
                filteredTimeClocks: departmentTimeClocks,
                managedEmployees: departmentEmployees
            };
        }

        // No permission to view time clocks
        return { filteredTimeClocks: [], managedEmployees: [] };
    }, [timeClocks, firebaseUser, organizationEmployees, canReadAllTimeClocks, canReadManagedTimeClocks, isUserDepartmentManager]);

    const handleWeekChange = (newDate) => {
        if (newDate && moment.isMoment(newDate)) {
            setCurrentDate(newDate.clone().startOf('week'));
        }
    };

    useEffect(() => {
        if (organization && currentDate) {
            const weekString = currentDate.clone().format('YYYY-MM-DD');
            const unsubscribe = fetchWeeklyTimeClocksSnapshotListener(
                organization.id,
                weekString,
                (timeClocks) => {
                    setTimeClocks(timeClocks);
                },
                setError,
                setLoading
            );

            return () => unsubscribe();
        }
    }, [organization, currentDate]);

    const handleOpenEditModal = (entry) => {
        const canEditAllTimeClocks = hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_ALL_TIME_CLOCKS);
        const canEditManagedTimeClocks = hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_TIME_CLOCKS_FOR_MANAGED_EMPLOYEES);
        
        if (!canEditAllTimeClocks && (!canEditManagedTimeClocks || !isUserDepartmentManager(firebaseUser?.uid))) return;
        
        // For department managers, verify the entry belongs to their department
        if (!canEditAllTimeClocks && canEditManagedTimeClocks) {
            const managerEmployee = organizationEmployees.find(emp => emp.id === firebaseUser.uid);
            const employeeToEdit = organizationEmployees.find(emp => emp.id === entry.employee_id);
            if (managerEmployee?.department_id !== employeeToEdit?.department_id) return;
        }
        
        setSelectedTimeClock({ 
            entry,
            docId: entry.id
        });
        setShowEditTimeClockModal(true);
    };

    const toggleEntry = (entryId) => {
        setExpandedEntries(prev => {
            const newSet = new Set(prev);
            if (newSet.has(entryId)) {
                newSet.delete(entryId);
            } else {
                newSet.add(entryId);
            }
            return newSet;
        });
    };

    const toggleDate = (dateId) => {
        setExpandedDates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(dateId)) {
                newSet.delete(dateId);
            } else {
                newSet.add(dateId);
            }
            return newSet;
        });
    };

    const handleEmployeeChange = (event) => {
        const value = event.target.value;
        setSelectedEmployee(value);
        // Reset expanded states when changing employee selection
        setExpandedEntries(new Set());
        setExpandedDates(new Set());
        setExpandedEmployees(new Set());
    };

    const calculateTotalDuration = (entries) => {
        let totalMinutes = 0;
        entries.forEach(entry => {
            if (entry.timestamp_in && entry.timestamp_out) {
                const duration = moment.duration(
                    moment(entry.timestamp_out.seconds * 1000).diff(
                        moment(entry.timestamp_in.seconds * 1000)
                    )
                );
                totalMinutes += duration.asMinutes();
            }
        });
        
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.round(totalMinutes % 60);
        
        return { hours, minutes };
    };

    const hasActiveClockIn = (entries) => {
        return entries.some(entry => entry.timestamp_in && !entry.timestamp_out);
    };

    // Group entries first by employee, then by date
    const groupedEntries = React.useMemo(() => {
        // First filter by selected employee if any
        const employeeFiltered = selectedEmployee 
            ? filteredTimeClocks.filter(entry => entry.employee_id === selectedEmployee)
            : filteredTimeClocks;

        // Then group by employee and date
        return employeeFiltered.reduce((result, entry) => {
            const employeeId = entry.employee_id;
            const entryDate = moment.unix(entry.timestamp_in.seconds).format('YYYY-MM-DD');

            if (!result[employeeId]) {
                result[employeeId] = {
                    dates: {}
                };
            }
            if (!result[employeeId].dates[entryDate]) {
                result[employeeId].dates[entryDate] = [];
            }
            result[employeeId].dates[entryDate].push(entry);

            return result;
        }, {});
    }, [filteredTimeClocks, selectedEmployee]);

    // Get the current week's start (Sunday) and end (Saturday) dates for display
    const weekStart = currentDate.clone().startOf('week');
    const weekEnd = weekStart.clone().endOf('week');

    const formatWeekDisplay = (start, end) => {
        if (start.month() === end.month()) {
            // Same month: "Dec 15 - 21, 2024"
            return `${start.format('MMM D')} - ${end.format('D, YYYY')}`;
        } else if (start.year() === end.year()) {
            // Different months, same year: "Dec 29 - Jan 4, 2024"
            return `${start.format('MMM D')} - ${end.format('MMM D, YYYY')}`;
        } else {
            // Different years: "Dec 29, 2023 - Jan 4, 2024"
            return `${start.format('MMM D, YYYY')} - ${end.format('MMM D, YYYY')}`;
        }
    };

    const toggleEmployee = (employeeId) => {
        setExpandedEmployees(prev => {
            const newSet = new Set(prev);
            if (newSet.has(employeeId)) {
                newSet.delete(employeeId);
            } else {
                newSet.add(employeeId);
            }
            return newSet;
        });
    };

    // Update the employee selection dropdown based on permissions
    const employeeOptions = React.useMemo(() => {
        if (canReadAllTimeClocks) {
            return organizationEmployees;
        }
        return managedEmployees;
    }, [canReadAllTimeClocks, organizationEmployees, managedEmployees]);

    if (error) return <div>{t(StringKeys.ERROR_LOADING_TIME_CLOCKS)}: {error.message}</div>;

    return (
        <AppSidebar
            content={
                <Box sx={{ p: 3 }}>
                    {!loading ? (
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant={isMobile ? "h6" : "h5"}>
                                        {t(StringKeys.REVIEW_TIME_CLOCK_SUBMISSIONS)}
                                    </Typography>
                                    {!isMobile ? (
                                        hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_ALL_TIME_CLOCKS) || 
                                        (hasPermission(Modules.REVIEW_TIME_CLOCKS, PermissionTypes.READ_AND_WRITE_TIME_CLOCKS_FOR_MANAGED_EMPLOYEES) && 
                                         isUserDepartmentManager(firebaseUser?.uid)) ? (
                                            <Button
                                                variant="contained"
                                                startIcon={<IconPlus />}
                                                onClick={() => setShowAddTimeClockModal(true)}
                                                sx={{ minWidth: 'fit-content' }}
                                            >
                                                {t(StringKeys.ADD_TIME_CLOCK_ENTRY)}
                                            </Button>
                                        ) : null
                                    ) : (
                                        <>
                                            <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                <MoreVert />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={() => handleMenuClose(setAnchorEl) }
                                            >
                                                <MenuItem onClick={() => { 
                                                    setShowAddTimeClockModal(true); 
                                                    handleMenuClose(setAnchorEl); 
                                                }}>
                                                    <ListItemIcon>
                                                        <IconPlus size={20} />
                                                    </ListItemIcon>
                                                    {t(StringKeys.ADD_TIME_CLOCK_ENTRY)}
                                                </MenuItem>
                                            </Menu>
                                        </>
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <LocalizationProvider dateAdapter={AdapterMoment}>
                                            <DatePicker
                                                label={t(StringKeys.SELECT_WEEK)}
                                                value={currentDate}
                                                onChange={handleWeekChange}
                                                format={formatWeekDisplay(weekStart, weekEnd)}
                                                views={['year', 'month', 'day']}
                                                sx={{ width: '100%' }}
                                                slotProps={{
                                                    textField: {
                                                        inputProps: {
                                                            value: formatWeekDisplay(weekStart, weekEnd)
                                                        }
                                                    }
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>{t(StringKeys.EMPLOYEE)}</InputLabel>
                                            <Select
                                                value={selectedEmployee}
                                                onChange={handleEmployeeChange}
                                                label={t(StringKeys.EMPLOYEE)}
                                                MenuProps={{
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 300
                                                        }
                                                    }
                                                }}
                                            >
                                                {employeeOptions.length > 0 ? [
                                                    <MenuItem key="all" value="">{t(StringKeys.ALL_EMPLOYEES)}</MenuItem>,
                                                    ...employeeOptions.map(emp => (
                                                        <MenuItem 
                                                            key={emp.id} 
                                                            value={emp.id}
                                                        >
                                                            {emp.first_name} {emp.last_name}
                                                        </MenuItem>
                                                    ))
                                                ] : (
                                                    <MenuItem disabled value="">
                                                        {t(StringKeys.NO_EMPLOYEES_ASSIGNED)}
                                                    </MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                {Object.keys(groupedEntries).length > 0 ? (
                                    Object.entries(groupedEntries).map(([employeeId, employeeData]) => {
                                        const employee = organizationEmployees.find(emp => emp.id === employeeId);
                                        
                                        return (
                                            <Box key={employeeId} sx={{ mb: 6 }}>
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between', 
                                                    alignItems: 'center',
                                                    borderBottom: '2px solid #eee', 
                                                    pb: 1, 
                                                    mb: 3,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                                    }
                                                }}
                                                onClick={() => toggleEmployee(employeeId)}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <IconButton 
                                                            size="small" 
                                                            sx={{ mr: 1, p: 0 }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleEmployee(employeeId);
                                                            }}
                                                        >
                                                            {expandedEmployees.has(employeeId) ? <IconArrowUp size={20} /> : <IconArrowDown size={20} />}
                                                        </IconButton>
                                                        <Typography variant="h6">
                                                            {employee?.first_name} {employee?.last_name}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="subtitle1" color="text.secondary">
                                                        {(() => {
                                                            const totalMinutes = Object.values(employeeData.dates).reduce((total, entries) => {
                                                                return total + entries.reduce((sum, entry) => {
                                                                    if (entry.timestamp_in && entry.timestamp_out) {
                                                                        return sum + moment.duration(
                                                                            moment(entry.timestamp_out.seconds * 1000).diff(
                                                                                moment(entry.timestamp_in.seconds * 1000)
                                                                            )
                                                                        ).asMinutes();
                                                                    }
                                                                    return sum;
                                                                }, 0);
                                                            }, 0);
                                                            const hours = Math.floor(totalMinutes / 60);
                                                            const minutes = Math.round(totalMinutes % 60);
                                                            return `${t(StringKeys.WEEKLY_TOTAL)}: ${hours}h ${minutes}m`;
                                                        })()}
                                                    </Typography>
                                                </Box>
                                                <Collapse in={expandedEmployees.has(employeeId)} timeout="auto" unmountOnExit>
                                                    {Object.entries(employeeData.dates)
                                                        .sort(([dateA], [dateB]) => moment(dateB).diff(moment(dateA)))
                                                        .map(([date, entries]) => {
                                                            const totalDuration = calculateTotalDuration(entries);
                                                            const dateId = `${employeeId}-${date}`;
                                                            const isExpanded = expandedDates.has(dateId);
                                                            const isActiveClockIn = hasActiveClockIn(entries);
                                                            
                                                            return (
                                                                <Box key={date} sx={{ mb: 4, ml: 2 }}>
                                                                    <Box 
                                                                        sx={{ 
                                                                            display: 'flex', 
                                                                            alignItems: 'center',
                                                                            borderLeft: '3px solid #ccc',
                                                                            pl: 2,
                                                                            py: 0.5,
                                                                            mb: isExpanded ? 2 : 0,
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(0, 0, 0, 0.02)'
                                                                            }
                                                                        }}
                                                                        onClick={() => toggleDate(dateId)}
                                                                    >
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                            <IconButton 
                                                                                size="small" 
                                                                                sx={{ mr: 1, p: 0 }}
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    toggleDate(dateId);
                                                                                }}
                                                                            >
                                                                                {isExpanded ? <IconArrowUp size={20} /> : <IconArrowDown size={20} />}
                                                                            </IconButton>
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                                                                <Typography 
                                                                                    variant="subtitle2" 
                                                                                    color="text.secondary"
                                                                                >
                                                                                    {moment(date).format('dddd, MMMM D, YYYY')}
                                                                                </Typography>
                                                                                {isActiveClockIn && (
                                                                                    <Typography 
                                                                                        variant="caption"
                                                                                        sx={{ 
                                                                                            color: 'error.main',
                                                                                            ml: 2,
                                                                                            fontWeight: 'medium'
                                                                                        }}
                                                                                    >
                                                                                        {t(StringKeys.CURRENTLY_CLOCKED_IN)}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                        <Typography 
                                                                            variant="subtitle2" 
                                                                            color="text.secondary"
                                                                            sx={{ 
                                                                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                                px: 1.5,
                                                                                py: 0.5,
                                                                                borderRadius: 1,
                                                                                ml: 2
                                                                            }}
                                                                        >
                                                                            {t(StringKeys.TOTAL)}: {totalDuration.hours}h {totalDuration.minutes}m
                                                                        </Typography>
                                                                    </Box>
                                                                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                                        <Box sx={{ ml: 3, mt: 2 }}>
                                                                            {entries
                                                                                .sort((a, b) => b.timestamp_in.seconds - a.timestamp_in.seconds)
                                                                                .map((entry) => (
                                                                                    <TimeClockEntry
                                                                                        key={entry.id}
                                                                                        entry={entry}
                                                                                        entryId={entry.id}
                                                                                        employee={organizationEmployees}
                                                                                        onEdit={handleOpenEditModal}
                                                                                        expanded={expandedEntries.has(entry.id)}
                                                                                        onToggle={toggleEntry}
                                                                                    />
                                                                                ))
                                                                            }
                                                                        </Box>
                                                                    </Collapse>
                                                                </Box>
                                                            );
                                                        })}
                                                </Collapse>
                                            </Box>
                                        );
                                    })
                                ) : (
                                    <Box sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'center', 
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        py: 4
                                    }}>
                                        <Typography variant="h6" color="text.secondary" gutterBottom>
                                            {selectedEmployee ? (
                                                <>
                                                    {t(StringKeys.NO_TIME_CLOCK_DATA_FOR_EMPLOYEE)}{' '}
                                                    {employeeOptions.find(emp => emp.id === selectedEmployee)?.first_name}{' '}
                                                    {employeeOptions.find(emp => emp.id === selectedEmployee)?.last_name}
                                                </>
                                            ) : (
                                                t(StringKeys.NO_TIME_CLOCK_DATA_TO_DISPLAY)
                                            )}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {t(StringKeys.FOR_SELECTED_WEEK)}: {formatWeekDisplay(weekStart, weekEnd)}
                                        </Typography>
                                        {selectedEmployee && (
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                <Button 
                                                    size="small" 
                                                    onClick={() => setSelectedEmployee('')}
                                                    sx={{ textTransform: 'none' }}
                                                >
                                                    {t(StringKeys.VIEW_ALL_EMPLOYEES)}
                                                </Button>
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    ) : (
                        <Loading />
                    )}
                    {selectedTimeClock.entry && (
                        <EditTimeClockModal
                            show={showEditTimeClockModal}
                            onClose={() => setShowEditTimeClockModal(false)}
                            docId={selectedTimeClock.docId}
                            timeClockEntry={selectedTimeClock.entry}
                        />
                    )}
                    <AddTimeClockModal
                        show={showAddTimeClockModal}
                        onClose={() => setShowAddTimeClockModal(false)}
                        organizationId={organization?.id}
                        employees={employeeOptions}
                    />
                </Box>
            }
        />
    );
}

export default ReviewTimeClocks;
